import {
  BoxGeometry,
  CapsuleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointLight,
  SphereGeometry,
  Vector3,
} from 'three';
import { lerp, lerpAngle } from '../util/math.js';
import { resolveObstacleCollision } from '../world/obstacles.js';

const geometries = {
  body: new CapsuleGeometry(0.43, 0.74, 5, 10),
  head: new SphereGeometry(0.36, 16, 12),
  hat: new CylinderGeometry(0.4, 0.46, 0.16, 16),
  capBrim: new BoxGeometry(0.48, 0.08, 0.22),
  arm: new BoxGeometry(0.2, 0.58, 0.2),
  leg: new BoxGeometry(0.22, 0.64, 0.24),
  shoe: new BoxGeometry(0.28, 0.16, 0.42),
  gun: new BoxGeometry(0.2, 0.18, 0.9),
  dash: new ConeGeometry(0.22, 0.72, 12),
};

const defaultColors = {
  skin: 0xffd7b1,
  shirt: 0x53ffb5,
  pants: 0x263b56,
  hat: 0xff3156,
  shoes: 0xdbe8f8,
  gun: 0xdbe8f8,
  projectile: 0x53ffb5,
};

const moveDir = new Vector3();
const nextPos = new Vector3();
const aimScratch = new Vector3();

let player = null;

export function initPlayer(scene, options = {}) {
  const colors = { ...defaultColors, ...options.colors };
  const materials = createPlayerMaterials(colors);
  const group = makePlayerModel(materials, colors);
  scene.add(group);

  player = {
    group,
    materials,
    colors,
    pos: new Vector3(0, 0, 0),
    vel: new Vector3(),
    aim: new Vector3(0, 0, -1),
    speed: 12.6,
    radius: 0.68,
    shotCooldown: 0,
    invuln: 0,
    bob: 0,
    dashTimer: 0,
    dashDir: new Vector3(0, 0, -1),
  };
  group.visible = options.visible ?? false;
  return player;
}

export function getPlayer() {
  if (!player) {
    throw new Error('[player] initPlayer() must run before getPlayer().');
  }
  return player;
}

export function resetPlayer(position = new Vector3()) {
  const activePlayer = getPlayer();
  activePlayer.pos.copy(position);
  activePlayer.vel.set(0, 0, 0);
  activePlayer.aim.set(0, 0, -1);
  activePlayer.shotCooldown = 0;
  activePlayer.invuln = 0;
  activePlayer.bob = 0;
  activePlayer.dashTimer = 0;
  activePlayer.dashDir.set(0, 0, -1);
  activePlayer.group.position.copy(activePlayer.pos);
  activePlayer.group.rotation.y = 0;
  activePlayer.group.visible = true;
}

export function updatePlayer(dt, options = {}) {
  const activePlayer = getPlayer();
  const {
    input = { keys: new Set(), touchMove: false },
    game = {},
    world = null,
    pointerWorld = null,
    autoTarget = null,
    onDashTrail = null,
  } = options;

  readMoveDirection(input.keys, moveDir);
  if (game.dashCooldown > 0) {
    const recovery = 1 + Math.max(0, game.upgrades?.moveSpeed || 0) * 0.45;
    game.dashCooldown = Math.max(0, game.dashCooldown - dt * recovery);
  }
  if (activePlayer.dashTimer > 0) activePlayer.dashTimer -= dt;

  const speedBoost = 1 + Math.max(0, game.upgrades?.moveSpeed || 0);
  const accel = activePlayer.speed * speedBoost * (input.touchMove ? 1.08 : 1) * (game.overdriveTime > 0 ? 1.28 : 1);
  activePlayer.vel.x = lerp(activePlayer.vel.x, moveDir.x * accel, 1 - Math.exp(-dt * 14));
  activePlayer.vel.z = lerp(activePlayer.vel.z, moveDir.z * accel, 1 - Math.exp(-dt * 14));

  nextPos.copy(activePlayer.pos).addScaledVector(activePlayer.vel, dt);
  if (activePlayer.dashTimer > 0) {
    nextPos.addScaledVector(activePlayer.dashDir, 31 * dt);
    onDashTrail?.(activePlayer.pos);
  }

  if (world) {
    nextPos.x = Math.max(-world.half + 2.2, Math.min(world.half - 2.2, nextPos.x));
    nextPos.z = Math.max(-world.half + 2.2, Math.min(world.half - 2.2, nextPos.z));
    resolveObstacleCollision(nextPos, activePlayer.radius, world);
  }

  activePlayer.pos.copy(nextPos);
  activePlayer.group.position.copy(activePlayer.pos);
  updateAim(activePlayer, pointerWorld, autoTarget, input.aimVector);

  activePlayer.group.rotation.y = lerpAngle(
    activePlayer.group.rotation.y,
    Math.atan2(activePlayer.aim.x, activePlayer.aim.z),
    1 - Math.exp(-dt * 18),
  );

  const moving = moveDir.lengthSq() > 0;
  activePlayer.bob += dt * (moving ? 12 : 4);
  activePlayer.group.position.y = Math.sin(activePlayer.bob) * (moving ? 0.06 : 0.025);
  if (activePlayer.invuln > 0) activePlayer.invuln -= dt;
}

export function dashPlayer(options = {}) {
  const activePlayer = getPlayer();
  const {
    input = { keys: new Set() },
    game = {},
    onDash = null,
  } = options;

  if (game.mode && game.mode !== 'playing') return false;
  if (game.dashCooldown > 0 || activePlayer.dashTimer > 0) return false;

  readMoveDirection(input.keys, moveDir);
  if (moveDir.lengthSq() < 0.001) moveDir.copy(activePlayer.aim);
  activePlayer.dashDir.copy(moveDir).normalize();
  activePlayer.dashTimer = 0.18;
  activePlayer.invuln = Math.max(activePlayer.invuln, 0.24);
  game.dashCooldown = 1.75;
  onDash?.(activePlayer);
  return true;
}

export function createPlayerInput() {
  return {
    keys: new Set(),
    gamepadKeys: new Set(),
    aimVector: null,
    touchMove: false,
    shooting: false,
  };
}

export function readMoveDirection(keys, target = new Vector3()) {
  target.set(0, 0, 0);
  if (keys.has('KeyW') || keys.has('ArrowUp')) target.z -= 1;
  if (keys.has('KeyS') || keys.has('ArrowDown')) target.z += 1;
  if (keys.has('KeyA') || keys.has('ArrowLeft')) target.x -= 1;
  if (keys.has('KeyD') || keys.has('ArrowRight')) target.x += 1;
  if (target.lengthSq() > 0) target.normalize();
  return target;
}

export function updatePlayerMaterials(colors) {
  const activePlayer = getPlayer();
  Object.assign(activePlayer.colors, colors);
  activePlayer.materials.shirt.color.set(activePlayer.colors.shirt);
  activePlayer.materials.pants.color.set(activePlayer.colors.pants);
  activePlayer.materials.hat.color.set(activePlayer.colors.hat);
  activePlayer.materials.shoes.color.set(activePlayer.colors.shoes);
  activePlayer.materials.projectile.color.set(activePlayer.colors.projectile);
  activePlayer.group.userData.glow?.color.set(activePlayer.colors.projectile);
}

function updateAim(activePlayer, pointerWorld, autoTarget, manualAim) {
  if (autoTarget?.alive) {
    aimScratch.copy(autoTarget.pos).sub(activePlayer.pos);
  } else if (manualAim && Math.hypot(manualAim.x, manualAim.z) > 0.001) {
    aimScratch.set(manualAim.x, 0, manualAim.z);
  } else if (pointerWorld) {
    aimScratch.copy(pointerWorld).sub(activePlayer.pos);
  } else {
    aimScratch.copy(activePlayer.aim);
  }
  aimScratch.y = 0;
  if (aimScratch.lengthSq() > 0.0001) activePlayer.aim.copy(aimScratch.normalize());
}

function createPlayerMaterials(colors) {
  return {
    skin: new MeshStandardMaterial({ color: colors.skin, roughness: 0.72 }),
    shirt: new MeshStandardMaterial({ color: colors.shirt, roughness: 0.62, metalness: 0.02 }),
    pants: new MeshStandardMaterial({ color: colors.pants, roughness: 0.75 }),
    hat: new MeshStandardMaterial({ color: colors.hat, roughness: 0.58, metalness: 0.02 }),
    shoes: new MeshStandardMaterial({ color: colors.shoes, roughness: 0.52, metalness: 0.08 }),
    gun: new MeshStandardMaterial({ color: colors.gun, roughness: 0.45, metalness: 0.36 }),
    projectile: new MeshBasicMaterial({ color: colors.projectile }),
  };
}

function makePlayerModel(materials, colors) {
  const group = new Group();
  const body = addPart(group, geometries.body, materials.shirt, [0, 1.14, 0]);
  addPart(group, geometries.head, materials.skin, [0, 1.92, 0]);
  addPart(group, geometries.hat, materials.hat, [0, 2.23, 0]);
  addPart(group, geometries.capBrim, materials.hat, [0, 2.18, -0.34]);
  const leftArm = addPart(group, geometries.arm, materials.skin, [-0.56, 1.18, -0.04], [0, 0, 0.2]);
  const rightArm = addPart(group, geometries.arm, materials.skin, [0.56, 1.18, -0.08], [0, 0, -0.2]);
  addPart(group, geometries.gun, materials.gun, [0, 1.16, -0.78]);
  const leftLeg = addPart(group, geometries.leg, materials.pants, [-0.19, 0.43, 0.05]);
  const rightLeg = addPart(group, geometries.leg, materials.pants, [0.19, 0.43, 0.05]);
  addPart(group, geometries.shoe, materials.shoes, [-0.19, 0.11, -0.08]);
  addPart(group, geometries.shoe, materials.shoes, [0.19, 0.11, -0.08]);

  const glow = new PointLight(new Color(colors.projectile), 1.4, 9, 2.2);
  glow.position.set(0, 1.1, -0.8);
  group.add(glow);
  group.userData.glow = glow;
  group.userData.parts = { body, leftArm, rightArm, leftLeg, rightLeg };
  return group;
}

function addPart(group, geometry, material, position, rotation = [0, 0, 0]) {
  const mesh = new Mesh(geometry, material);
  mesh.position.set(position[0], position[1], position[2]);
  mesh.rotation.set(rotation[0], rotation[1], rotation[2]);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.proceduralPlayer = true;
  group.add(mesh);
  return mesh;
}
